/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React, { useState } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { calculateThumbPosition, EUI_THUMB_SIZE } from './utils';

var calculateOffset = function calculateOffset(position, value, bound) {
  var threshold = 30;
  var offset = value === bound ? 0 : EUI_THUMB_SIZE / 2;

  if (offset !== 0) {
    // Estimating offset by eye. Trying to account for range scaling at both ends.
    offset = position <= threshold ? offset + 1 / position * threshold : offset;
    offset = position >= 100 - threshold ? offset - 1 / (100 - position) * threshold : offset;
  }

  return offset;
};

export var LEVEL_COLORS = ['primary', 'success', 'warning', 'danger'];
export var EuiRangeLevels = ({
  levels = [],
  max,
  min,
  showTicks,
  compressed
}) => {
  const [trackWidth, setTrackWidth] = useState(0);

  const handleRef = node => {
    setTrackWidth(node?.clientWidth ?? 0);
  };

  const validateLevelIsInRange = level => {
    if (level.min < min) {
      throw new Error(`The level min of ${level.min} is lower than the min value of ${min}.`);
    }

    if (level.max > max) {
      throw new Error(`The level max of ${level.max} is higher than the max value of ${max}.`);
    }
  };

  const classes = classNames('euiRangeLevels', {
    'euiRangeLevels--hasTicks': showTicks,
    'euiRangeLevels--compressed': compressed
  });
  return <div className={classes} ref={handleRef}>
      {levels.map((level, index) => {
      validateLevelIsInRange(level);
      const {
        color,
        className,
        min: levelMin,
        max: levelMax,
        ...rest
      } = level;
      let left = 0;
      let right = 0;
      let leftOffset = 0;
      let rightOffset = 0;

      if (trackWidth > 0) {
        left = levelMin === min ? 0 : calculateThumbPosition(levelMin, min, max, trackWidth);
        leftOffset = calculateOffset(left, levelMin, min);
        right = levelMax === max ? 100 : calculateThumbPosition(levelMax, min, max, trackWidth);
        rightOffset = calculateOffset(right, levelMax, max);
      }

      const isNamedColor = LEVEL_COLORS.includes(color);
      const styles = {
        left: `calc(${left}% + ${leftOffset}px)`,
        right: `calc(${100 - right}% - ${rightOffset}px)`,
        backgroundColor: !isNamedColor ? color : undefined
      };
      const levelClasses = classNames('euiRangeLevel', {
        'euiRangeLevel--customColor': !isNamedColor,
        [`euiRangeLevel--${color}`]: isNamedColor
      }, className);
      return <span key={index} style={styles} className={levelClasses} {...rest} />;
    })}
    </div>;
};
EuiRangeLevels.propTypes = {
  /**
     * An array of #EuiRangeLevel objects
     */
  levels: PropTypes.arrayOf(PropTypes.shape({
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,

    /**
       * Accepts one of `["primary", "success", "warning", "danger"]` or a valid CSS color value.
       */
    color: PropTypes.oneOfType([PropTypes.oneOf(["primary", "success", "warning", "danger"]).isRequired, PropTypes.any.isRequired]).isRequired,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }).isRequired),
  max: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  showTicks: PropTypes.bool,
  compressed: PropTypes.bool
};

try {
  EuiRangeLevels.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/form/range/range_levels.tsx',
    description: '',
    displayName: 'EuiRangeLevels',
    methods: [],
    props: {
      levels: {
        defaultValue: {
          value: '[]'
        },
        description: 'An array of #EuiRangeLevel objects',
        name: 'levels',
        parent: {
          fileName: 'app/src/components/form/range/range_levels.tsx',
          name: 'EuiRangeLevelsProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_levels.tsx',
          name: 'EuiRangeLevelsProps'
        }],
        required: false,
        type: {
          name: 'EuiRangeLevel[]'
        }
      },
      max: {
        defaultValue: null,
        description: '',
        name: 'max',
        parent: {
          fileName: 'app/src/components/form/range/range_levels.tsx',
          name: 'EuiRangeLevelsProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_levels.tsx',
          name: 'EuiRangeLevelsProps'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      min: {
        defaultValue: null,
        description: '',
        name: 'min',
        parent: {
          fileName: 'app/src/components/form/range/range_levels.tsx',
          name: 'EuiRangeLevelsProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_levels.tsx',
          name: 'EuiRangeLevelsProps'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      showTicks: {
        defaultValue: null,
        description: '',
        name: 'showTicks',
        parent: {
          fileName: 'app/src/components/form/range/range_levels.tsx',
          name: 'EuiRangeLevelsProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_levels.tsx',
          name: 'EuiRangeLevelsProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      compressed: {
        defaultValue: null,
        description: '',
        name: 'compressed',
        parent: {
          fileName: 'app/src/components/form/range/range_levels.tsx',
          name: 'EuiRangeLevelsProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_levels.tsx',
          name: 'EuiRangeLevelsProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['EuiRangeLevelsProps']
  };
} catch (e) {}